/**
 * P4-WP-8C.7: invitation continuation across authentication.
 *
 * An existing MissionAssist identity opening an invitation must sign in before
 * the pending membership can be claimed. That round trip leaves /accept-invite,
 * so the token has to survive it.
 *
 * This is CONTINUATION STATE, never authorization. Possessing the value here
 * grants nothing: `POST /auth/invites/claim` independently revalidates the
 * token, its expiry, its cancellation and acceptance state, and that the
 * authenticated session's normalized email is the invited one. A token planted
 * in storage by anyone reaches exactly as far as that endpoint's checks.
 *
 * Deliberately NOT a widening of `storePostLoginRedirect`, which is scoped to
 * /app paths. Widening it to arbitrary destinations would turn every 401 into
 * a general-purpose redirect store; this holds one token, for one screen, and
 * the destination is a fixed internal path that is never taken from input --
 * so it cannot become an open redirect.
 *
 * sessionStorage, not localStorage: the continuation dies with the tab, which
 * is the intended lifetime of "I am part-way through accepting an invitation".
 */

const KEY = "invite_continuation_token";

/** Park the token for the authentication round trip. Never logged. */
export function storeInviteContinuation(token) {
  if (typeof window === "undefined") return;
  if (typeof token !== "string" || !token.trim()) return;
  try {
    window.sessionStorage.setItem(KEY, token);
  } catch {
    // Private mode / storage disabled. The user simply re-opens the emailed
    // link after signing in; nothing breaks and nothing is granted.
  }
}

/**
 * Read the parked token WITHOUT consuming it.
 *
 * The caller decides when the continuation is spent, because a failed claim
 * should not silently strand the user.
 */
export function peekInviteContinuation() {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(KEY);
    return typeof value === "string" && value.trim() ? value : null;
  } catch {
    return null;
  }
}

/** Clear it. Safe to call when nothing is parked. */
export function clearInviteContinuation() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // nothing to do
  }
}

/**
 * The post-authentication destination for a parked invitation, or null.
 *
 * The path is a CONSTANT; only the token is interpolated, and it is encoded.
 * There is no way for stored state to redirect anywhere but this one screen.
 */
export function inviteContinuationPath() {
  const token = peekInviteContinuation();
  if (!token) return null;
  return `/accept-invite?token=${encodeURIComponent(token)}`;
}
